import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
    IconButton
} from "@mui/material";

/* ✅ ProDev Assets & Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import StopIcon from "@mui/icons-material/Stop";

const bgImage = "https://images.unsplash.com/photo-1633356122544-f134324a6cee";

/* ✅ Styled Components - ProDev Dark Glass Theme */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    "&:hover": { backgroundColor: "#e0b200", transform: "scale(1.05)" },
}));

const LiveDemoBox = styled(Box)(({ theme }) => ({
    padding: "20px",
    borderRadius: "12px",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    border: "1px dashed goldenrod",
    textAlign: "center",
    marginTop: "15px"
}));

/**
 * SimpleReactComponent
 * Specialized ProDevConsult Module
 * Author: Hazzan
 */
export default function SimpleReactComponent() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* ✅ React Hooks Implementation */
    const [count, setCount] = useState(1);
    const [message, setMessage] = useState("ProDev System Initializing...");
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [isSpeaking, setIsSpeaking] = useState(false);

    /* ✅ useEffect Hook: Lifecycle Management */
    useEffect(() => {
        const timer = setTimeout(() => {
            setMessage("ProDev Status: React Core Active! ✅");
        }, 2000);

        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);

        return () => {
            clearTimeout(timer);
            window.removeEventListener("scroll", checkScroll);
        };
    }, []);

    const handleSpeak = (text) => {
        window.speechSynthesis.cancel();
        if (isSpeaking) { setIsSpeaking(false); return; }
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        window.speechSynthesis.speak(utterance);
    };

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const increaseCount = () => {
        if (count < 4) setCount(prev => prev + 1);
    };

    const reactModules = useMemo(() => [
        {
            title: "1. Pure Functional Component",
            tag: "Stateless UI",
            desc: "The simplest form of a component. It takes data and returns JSX. Clean, predictable, and incredibly fast to render.",
            code: `const ProDevGreeting = () => {\n  return <h1>Hello from Hazzan!</h1>;\n};`
        },
        {
            title: "2. Component with Props",
            tag: "Dynamic Data",
            desc: "Props allow you to pass data into components. Think of them as function arguments that make your UI reusable across the ProDev ecosystem.",
            code: `const MemberCard = ({ name, role }) => {\n  return <h1>\${name}: \${role}</h1>;\n};`
        },
        {
            title: "3. State Management (useState)",
            tag: "Reactive UI",
            desc: "The 'useState' hook allows components to 'remember' information. When state changes, React automatically re-renders the UI to reflect the new data.",
            code: `const Counter = () => {\n  const [count, setCount] = useState(1);\n  \n  return (\n    <button onClick={() => setCount(count + 1)}>\n      Count: {count}\n    </button>\n  );\n};`
        },
        {
            title: "4. Lifecycle & Effects (useEffect)",
            tag: "Side Effects",
            desc: "The 'useEffect' hook manages side effects like data fetching or manual DOM changes. It runs after the component renders.",
            code: `useEffect(() => {\n  // Logic to fetch ProDev data...\n  console.log("Component Mounted");\n}, []); // Empty array ensures it runs once`
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h4" : "h3"} sx={{
                    fontWeight: 800, backgroundColor: "goldenrod", color: "#100720",
                    px: { xs: 3, md: 6 }, py: 1.5, borderRadius: "999px",
                    display: "inline-block", boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                }}>
                    REACT ARCHITECTURE
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h5" fontWeight={800} color="goldenrod">
                                Functional Component Overview
                            </Typography>
                            <IconButton onClick={() => handleSpeak("React functional components are simple, reusable, and efficient. In this module, Hazzan demonstrates state, props, and effect hooks.")} sx={{ color: "goldenrod" }}>
                                <VolumeUpIcon />
                            </IconButton>
                        </Stack>
                        <Typography variant="body1" sx={{ opacity: 0.9, lineHeight: 1.7 }}>
                            Functional components have become the industry standard. By utilizing <b>Hooks</b>, we can write less code while achieving more powerful logic. This module explores the four pillars of modern React development at <b>ProDevConsult</b>.
                        </Typography>
                    </SectionCard>
                </Grid>

                {reactModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}><AiOutlineRead /></Avatar>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                        <Typography variant="caption" color="goldenrod" fontWeight={800}>{item.tag}</Typography>
                                    </Box>
                                </Box>
                                <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                    <IconButton onClick={() => handleCopy(item.code, i)} sx={{ color: "white" }}>
                                        {copiedIndex === i ? <CheckIcon /> : <ContentCopyIcon />}
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>

                            {/* Live Demos for State and Effect */}
                            {i === 2 && (
                                <LiveDemoBox>
                                    <Typography variant="h6" sx={{ color: "goldenrod", mb: 2 }}>Current State: {count}</Typography>
                                    <Button
                                        variant="contained"
                                        sx={{ bgcolor: "goldenrod", color: "black", fontWeight: "bold" }}
                                        onClick={increaseCount}
                                        disabled={count >= 4}
                                    >
                                        Increase State (Max: 4)
                                    </Button>
                                </LiveDemoBox>
                            )}
                            {i === 3 && (
                                <LiveDemoBox>
                                    <Typography variant="h6" sx={{ color: "goldenrod" }}>{message}</Typography>
                                </LiveDemoBox>
                            )}
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center" sx={{ mt: 4, mb: 10 }}>
                    <Link to="/javascriptcoding" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">COMPLETE MASTERY SERIES</ActionButton>
                    </Link>
                </Grid>
            </Grid>

            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                {isSpeaking && (
                    <Zoom in={isSpeaking}>
                        <Fab size="medium" color="error" onClick={() => window.speechSynthesis.cancel()}><StopIcon /></Fab>
                    </Zoom>
                )}
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: "goldenrod" }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </PageWrapper>
    );
}
